import { useState, useEffect } from 'react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Bell, Mail, Smartphone, Moon, Save } from 'lucide-react';
import { getNotificationSettings, updateNotificationSettings } from '@/lib/adminApi';
import { toast } from 'react-toastify';

interface NotificationSetting {
  id: string;
  title: string;
  description: string;
  email: boolean;
  push: boolean;
  sms: boolean;
}

export function SettingsNotifications() {
  const [settings, setSettings] = useState<NotificationSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const { settings } = await getNotificationSettings();
        setSettings(settings);
      } catch (error) {
        console.error('Failed to load notification settings:', error);
        toast.error('Failed to load settings');
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, []);

  const toggleSetting = (id: string, channel: 'email' | 'push' | 'sms') => {
    setSettings(settings.map(s => 
      s.id === id ? { ...s, [channel]: !s[channel] } : s
    ));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateNotificationSettings(settings);
      toast.success('Notification preferences saved');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-muted-foreground">Loading preferences...</div>;
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-white whitespace-nowrap">Notifications</h1>
        <p className="text-muted-foreground mt-1 sm:mt-2 text-sm sm:text-base">Manage how and when you receive alerts</p>
      </div>

      <div className="grid grid-cols-1 gap-8">
        {/* Notification Channels */}
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="p-6 border-b border-border bg-card/50">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Bell className="w-5 h-5 text-[#8FB560]" />
              Alert Preferences
            </h2>
          </div>
          
          <div className="divide-y divide-border">
            {/* Header Row */}
            <div className="grid grid-cols-12 gap-4 p-4 bg-background/30 text-xs font-medium text-muted-foreground uppercase tracking-wider">
              <div className="col-span-6 md:col-span-5">Category</div>
              <div className="col-span-2 text-center flex justify-center items-center gap-1">
                <Mail className="w-3 h-3" /> Email
              </div>
              <div className="col-span-2 text-center flex justify-center items-center gap-1">
                <Bell className="w-3 h-3" /> Push
              </div>
              <div className="col-span-2 text-center flex justify-center items-center gap-1">
                <Smartphone className="w-3 h-3" /> SMS
              </div>
            </div>

            {/* Settings Rows */}
            {settings.map((setting) => (
              <div key={setting.id} className="grid grid-cols-12 gap-4 p-6 items-center hover:bg-muted/30 transition-colors">
                <div className="col-span-6 md:col-span-5">
                  <h3 className="text-sm font-medium text-white">{setting.title}</h3>
                  <p className="text-xs text-muted-foreground mt-1">{setting.description}</p>
                </div>
                <div className="col-span-2 flex justify-center">
                  <Switch 
                    checked={setting.email} 
                    onCheckedChange={() => toggleSetting(setting.id, 'email')}
                    className="data-[state=checked]:bg-[#8FB560]"
                  />
                </div>
                <div className="col-span-2 flex justify-center">
                  <Switch 
                    checked={setting.push} 
                    onCheckedChange={() => toggleSetting(setting.id, 'push')}
                    className="data-[state=checked]:bg-[#8FB560]"
                  />
                </div>
                <div className="col-span-2 flex justify-center">
                  <Switch 
                    checked={setting.sms} 
                    onCheckedChange={() => toggleSetting(setting.id, 'sms')}
                    className="data-[state=checked]:bg-[#8FB560]"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quiet Hours */}
        <div className="bg-card border border-border rounded-xl p-6">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <Moon className="w-5 h-5 text-purple-400" />
                Quiet Hours
              </h2>
              <p className="text-muted-foreground text-sm mt-1">
                Pause notifications during specific times (except critical alerts).
              </p>
            </div>
            <Switch className="data-[state=checked]:bg-purple-500" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 opacity-50 pointer-events-none">
            <div className="space-y-2">
              <Label>Start Time</Label>
              <div className="p-3 bg-background border border-border rounded-md text-muted-foreground">
                10:00 PM
              </div>
            </div>
            <div className="space-y-2">
              <Label>End Time</Label>
              <div className="p-3 bg-background border border-border rounded-md text-muted-foreground">
                07:00 AM
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline">Reset to Default</Button>
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="bg-[#8FB560] hover:bg-[#7a9d51]"
          >
            {saving ? (
              <>Saving...</>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Preferences
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

